import { Component, EventEmitter, Input, OnInit, Output } from '@angular/core';
import { TranslateService } from '@ngx-translate/core';
import {
  LastPaymentMethod,
  Terms,
} from '@payments/constants/payment-dashboard';
import { DashboardFilterIds } from '@payments/pages/payment-dashboard/services/payment-dashboard.service';
import { CustomerMetrics } from '@usf/payment-types';
import { BehaviorSubject } from 'rxjs';

export interface DashboardSearchModel {
  customerMetrics: CustomerMetrics[];
  setFilters?: (response: DashboardSearchResponse) => void;
}

export interface DashboardSearchItem {
  filter: DashboardFilterIds;
  value: number | string | number[] | string[];
}

export interface DashboardSearchResponse {
  items: DashboardSearchItem[];
  searchText: string;
}

export interface SearchGroupItem {
  name: string;
  needsTitleCase: boolean;
  value: any;
}

export interface SearchGroup {
  filter:
    | DashboardFilterIds.customer
    | DashboardFilterIds.lastPaymentMethod
    | DashboardFilterIds.terms;
  items: SearchGroupItem[];
  name: string;
}

@Component({
  template: '',
})
export class DashboardSearchComponent implements OnInit {
  @Input() customerMetrics: CustomerMetrics[];
  @Output() setFilters?: (response: DashboardSearchResponse) => void;
  @Output() setFiltersEmitter = new EventEmitter<DashboardSearchResponse>();
  @Output() inputBlurEmitter = new EventEmitter<void>();

  readonly minLength = 2;

  activeItem?: SearchGroupItem;
  isTypeAheadVisible = false;
  searchGroups: SearchGroup[] = [];
  searchText = '';
  searchText$ = new BehaviorSubject<string>('');
  searchPaymentMethods: string[] = [];
  searchTerms: string[] = [];

  constructor(readonly translateService: TranslateService) {}

  ngOnInit() {
    for (let key in LastPaymentMethod) {
      this.searchPaymentMethods.push(
        this.translateService.instant(
          'i18n.payments.paymentDashboard.lastPaymentMethod.' + key,
        ),
      );
    }
    for (let key in Terms) {
      this.searchTerms.push(Terms[key]);
    }
  }

  handleArrowKeys(event: KeyboardEvent) {
    switch (event.key) {
      case 'ArrowDown':
        event.preventDefault();
        this.setActiveItem(1);
        break;
      case 'ArrowUp':
        event.preventDefault();
        this.setActiveItem(-1);
        break;
      case 'Enter':
        if (this.activeItem) {
          const activeGroup = this.getActiveGroup(this.activeItem);
          this.selectItem(this.activeItem, activeGroup);
        }
        break;
    }
  }

  handleInputBlur(_event: any) {
    setTimeout(() => {
      this.isTypeAheadVisible = false;
      this.activeItem = undefined;
      this.inputBlurEmitter.emit();
    }, 200);
  }

  handleInputFocus(event: any) {
    const search = this.getSearchText(event);
    if (search.length >= this.minLength && this.searchGroups.length > 0) {
      this.isTypeAheadVisible = true;
    }
  }

  search(event: any) {
    const search = this.getSearchText(event).toLowerCase();
    if (search.length >= this.minLength) {
      let groups: SearchGroup[] = [];
      const customers = this.customerMetrics
        .filter(
          metrics =>
            metrics.customerName.toLowerCase().includes(search) ||
            metrics.customerNumber.toString().includes(search),
        )
        .map(metrics => {
          return {
            name: `${metrics.customerName} (\u00A0${metrics.customerNumber}\u00A0)`,
            needsTitleCase: true,
            value: metrics.customerNumber,
          } as SearchGroupItem;
        });

      const paymentMethods = this.searchPaymentMethods
        .filter(paymentMethod => paymentMethod.toLowerCase().includes(search))
        .map(paymentMethod => {
          return {
            name: paymentMethod,
            needsTitleCase: false,
            value: paymentMethod,
          } as SearchGroupItem;
        });

      if (customers.length > 0) {
        groups.push({
          filter: DashboardFilterIds.customer,
          items: customers,
          name: this.translateService.instant(
            'i18n.payments.paymentDashboard.search.customers',
          ),
        });
      }
      if (paymentMethods.length > 0) {
        groups.push({
          filter: DashboardFilterIds.lastPaymentMethod,
          items: paymentMethods,
          name: this.translateService.instant(
            'i18n.payments.paymentDashboard.search.paymentMethod',
          ),
        });
      }
      this.searchGroups = groups;
    } else {
      this.searchGroups = [];
    }
    if (this.searchGroups.length > 0) {
      this.isTypeAheadVisible = true;
    }
  }

  selectItem(item: SearchGroupItem, group: SearchGroup) {
    const searchItem = [
      {
        filter: group.filter,
        value: [item.value],
      } as DashboardSearchItem,
    ];

    this.sendResponse(searchItem);
  }

  selectFilteredItems() {
    if (this.searchText.length < this.minLength || this.activeItem) {
      return;
    }

    const searchItems = this.searchGroups.map(group => {
      return {
        filter: group.filter,
        value: group.items.map(item => item.value),
      } as DashboardSearchItem;
    });

    this.sendResponse(searchItems);
  }

  private getActiveGroup(groupItem: SearchGroupItem): SearchGroup {
    const activeGroup = this.searchGroups.find(group =>
      group.items.find(item => this.activeItem.value === item.value),
    );
    return activeGroup;
  }

  private getSearchText(event: any): string {
    return ((event.currentTarget?.value ?? '') as string).trim();
  }

  private sendResponse(searchItems: DashboardSearchItem[]) {
    const response = {
      items: searchItems,
      searchText: this.searchText,
    } as DashboardSearchResponse;
    this.setFiltersEmitter.emit(response);
    if (!!this.setFilters) {
      this.setFilters(response);
    }

    this.activeItem = undefined;
    this.isTypeAheadVisible = false;
    this.searchText = '';
  }

  private setActiveItem(direction: -1 | 1) {
    if (!this.activeItem) {
      const searchGroup = this.searchGroups[0] ?? undefined;
      if (searchGroup) {
        this.activeItem = searchGroup.items[0] ?? undefined;
      }
      return;
    }

    // Wrapped in a try in case keyboard event fires before type ahead appears
    try {
      const activeGroup = this.getActiveGroup(this.activeItem);
      const groupIndex = this.searchGroups.findIndex(
        group => group.name === activeGroup.name,
      );
      const itemIndex = activeGroup.items.findIndex(
        item => item.value === this.activeItem.value,
      );

      const newItem = activeGroup.items[itemIndex + direction];
      if (!!newItem) {
        this.activeItem = newItem;
      } else {
        const newGroup = this.searchGroups[groupIndex + direction];
        if (direction === -1) {
          if (!!newGroup) {
            this.activeItem = newGroup.items[newGroup.items.length - 1];
          } else {
            this.activeItem = undefined;
            this.isTypeAheadVisible = false;
          }
        } else {
          if (!!newGroup) {
            this.activeItem = newGroup.items[0];
          }
        }
      }
    } catch (_) {}

    setTimeout(() => {
      const activeElement = document.querySelector('.type-ahead li.active');
      if (!!activeElement) {
        activeElement.scrollIntoView({
          behavior: 'smooth',
          block: 'nearest',
        });
      }
    }, 100);
  }
}
